// FullMap — mapa con todo el viaje
import { useMemo, useState, useEffect } from "react";
import { TRIP as STATIC_TRIP } from "../data/trip";
import MapView from "../components/MapView";
import { useGeolocation, distanceMeters, formatDistance, walkingMinutes } from "../hooks/useGeolocation";
import { Link } from "react-router-dom";
import { Navigation, ChevronRight, Camera } from "lucide-react";
import { useTripBase } from "../hooks/useTripBase";
import { useTrip } from "../auth/TripContext";

// Approximate centers for known cities (used when no coordinates in markers).
const CITY_CENTERS = {
  "Budapest": [47.4979, 19.0402],
  "Praga": [50.0875, 14.4214],
  "Madrid": [40.4168, -3.7038],
  "Barcelona": [41.3851, 2.1734],
  "Sevilla": [37.3886, -5.9823],
  "Málaga": [36.7213, -4.4213],
  "Granada": [37.1773, -3.5986],
  "Ronda": [36.7421, -5.1664],
  "Cádiz": [36.5297, -6.2927],
  "Setenil de las Bodegas": [36.8611, -5.1803],
  "Vejer de la Frontera": [36.2502, -5.9671],
  "Tel Aviv": [32.0853, 34.7818],
  "Jerusalén": [31.7683, 35.2137],
};

export default function FullMap() {
  const base = useTripBase();
  const { trip: ctxTrip } = useTrip();
  const trip = ctxTrip || {};
  const { position, permission } = useGeolocation();

  // Build city filter from this trip's cities
  const tripCities = useMemo(() => {
    const set = new Set();
    Object.values(trip.places || {}).forEach((p) => { if (p?.city) set.add(p.city); });
    (trip.hotels || []).forEach((h) => { if (h?.city) set.add(h.city); });
    return Array.from(set);
  }, [trip]);
  const CITIES = useMemo(() => [
    { id: "all", label: "Todo", center: [40, 0], zoom: 3 },
    ...tripCities.map((c) => ({
      id: c, label: c,
      center: CITY_CENTERS[c] || [40, 0],
      zoom: 13,
    })),
  ], [tripCities]);

  const [city, setCity] = useState("all");
  useEffect(() => {
    if (tripCities.length && !tripCities.includes(city) && city !== "all") {
      setCity(tripCities[0]);
    }
  }, [tripCities, city]);
  const cityCfg = CITIES.find((c) => c.id === city) || CITIES[0];

  const places = useMemo(() => {
    return Object.values(trip.places || {}).filter((p) => city === "all" || p.city === city);
  }, [city, trip]);

  const markers = places.map((p) => ({
    id: p.id,
    lat: p.lat,
    lng: p.lng,
    emoji: p.icon || "📍",
    name: p.name,
    kind: p.jewish ? "jewish" : p.type === "food" ? "food" : p.type === "hotel" ? "hotel" : "",
  }));

  const hotelMarkers = (trip.hotels || [])
    .filter((h) => city === "all" || h.city === city)
    .map((h) => ({ id: h.id, lat: h.lat, lng: h.lng, emoji: "🏨", name: h.name, kind: "hotel" }));

  const allMarkers = [...markers, ...hotelMarkers].filter((m) => m.lat && m.lng);

  const nearby = useMemo(() => {
    if (!position) return [];
    return places
      .filter((p) => p.lat && p.lng)
      .map((p) => ({ p, d: distanceMeters(position, { lat: p.lat, lng: p.lng }) }))
      .sort((a, b) => a.d - b.d)
      .slice(0, 5);
  }, [position, places]);

  return (
    <div className="pb-28" data-testid="full-map-page">
      <div className="container-app pt-6 pb-3">
        <h1 className="text-3xl font-bold leading-tight">Mapa del viaje</h1>
        <p className="text-sm text-[hsl(var(--muted))] mt-1">Filtra por ciudad. Toca un punto para ver detalle.</p>
      </div>

      {/* City filter */}
      <div className="container-app flex gap-2 mb-3 overflow-x-auto hide-scrollbar pb-1">
        {CITIES.map((c) => (
          <button
            key={c.id}
            onClick={() => setCity(c.id)}
            className={`px-4 py-1.5 rounded-full text-sm font-medium whitespace-nowrap transition-all ${
              city === c.id
                ? "bg-[hsl(var(--ink))] text-[hsl(var(--bg))]"
                : "bg-[hsl(var(--bg-card))] text-[hsl(var(--ink))] border border-[hsl(var(--line))]"
            }`}
            data-testid={`map-city-${c.id}`}
          >
            {c.label}
          </button>
        ))}
      </div>

      {/* Map */}
      <div className="px-2 mb-4">
        <div className="card-paper overflow-hidden relative" style={{ height: 380 }}>
          <MapView
            center={cityCfg.center}
            zoom={cityCfg.zoom}
            markers={allMarkers}
            userPosition={position}
            height="100%"
            fitBounds={city !== "all"}
            onMarkerClick={(id) => {
              if (id.startsWith("h")) window.location.href = "/logistica";
              else window.location.href = `/lugar/${id}`;
            }}
          />
          {/* AR button — floating over the map */}
          <Link
            to={`${base}/ar`}
            className="absolute bottom-3 right-3 z-[500] pl-3 pr-4 py-2.5 rounded-full bg-[hsl(var(--ink))] text-[hsl(var(--bg))] shadow-xl flex items-center gap-2 text-sm font-semibold active:scale-95 transition-transform"
            data-testid="map-ar-btn"
          >
            <Camera size={16} />
            <span>AR</span>
          </Link>
        </div>
      </div>

      {/* GPS */}
      <div className="container-app">
        {permission === "granted" && position ? (
          <p className="text-xs flex items-center gap-2 text-[hsl(var(--muted))] mb-3" data-testid="map-gps-status">
            <Navigation size={12} className="text-[hsl(140_60%_45%)]" /> GPS activo · precisión ±{Math.round(position.accuracy)} m
          </p>
        ) : permission === "denied" ? (
          <p className="text-xs text-[hsl(var(--muted))] mb-3">📍 GPS denegado — actívalo en Ajustes → Safari para que el mapa te ubique.</p>
        ) : (
          <p className="text-xs text-[hsl(var(--muted))] mb-3">📍 Solicitando ubicación...</p>
        )}

        {/* Nearby */}
        {nearby.length > 0 && (
          <section className="mb-5">
            <h3 className="section-title mb-3">Lo más cerca de ti</h3>
            <ul className="space-y-2">
              {nearby.map(({ p, d }) => (
                <li key={p.id}>
                  <Link to={`${base}/lugar/${p.id}`} className="card-paper p-3 flex items-center gap-3 active:scale-[0.98]" data-testid={`nearby-${p.id}`}>
                    <span className="text-xl">{p.icon}</span>
                    <div className="flex-1 min-w-0">
                      <p className="font-medium text-sm truncate">{p.name}</p>
                      <p className="text-xs text-[hsl(var(--muted))]">{p.city}</p>
                    </div>
                    <div className="text-right shrink-0">
                      <p className="text-sm font-bold text-[hsl(var(--accent))]">{formatDistance(d)}</p>
                      <p className="text-[10px] text-[hsl(var(--muted))]">{walkingMinutes(d)} min</p>
                    </div>
                    <ChevronRight size={14} className="text-[hsl(var(--muted))]" />
                  </Link>
                </li>
              ))}
            </ul>
          </section>
        )}

        {/* List by type */}
        <section>
          <h3 className="section-title mb-3">{places.length} lugares · {city}</h3>
          <ul className="space-y-2">
            {places.map((p) => (
              <li key={p.id}>
                <Link to={`${base}/lugar/${p.id}`} className="card-paper p-3 flex items-center gap-3 active:scale-[0.98]" data-testid={`map-list-${p.id}`}>
                  <span className="text-xl">{p.icon}</span>
                  <div className="flex-1 min-w-0">
                    <p className="font-medium text-sm truncate">{p.name}</p>
                    <p className="text-xs text-[hsl(var(--muted))] truncate">
                      {p.jewish && "✡ "}{p.cuisine || (p.type === "synagogue" ? "Sinagoga" : p.type === "memorial" ? "Memorial" : p.type === "food" ? "Restaurante" : "Lugar")}
                    </p>
                  </div>
                  <ChevronRight size={14} className="text-[hsl(var(--muted))]" />
                </Link>
              </li>
            ))}
          </ul>
        </section>
      </div>
    </div>
  );
}
